import { getLogger } from 'meet-hour-logger';
import type { Dispatch } from 'redux';

import UIEvents from '../../../service/UI/UIEvents';
import {
    AUDIO_MUTE,
    VIDEO_MUTE,
    createRemoteMuteConfirmedEvent,
    createToolbarEvent,
    sendAnalytics
} from '../analytics';
import {
    MODERATOR_UNLOCK_AUDIO,
    MODERATOR_UNLOCK_VIDEO,
    forcemuteRemoteParticipant,
    moderatorForceMutedUsAll,
    moderatorunclockAudio,
    moderatorunclockVideo
} from '../base/conference';
import {
    MEDIA_TYPE,
    VIDEO_MUTISM_AUTHORITY,
    setAudioMuted,
    setVideoMuted
} from '../base/media';
import {
    getLocalParticipant,
    getParticipants,
    isLocalParticipantForceMutedAudio,
    isLocalParticipantForceMutedVideo,
    muteRemoteParticipant
} from '../base/participants';

declare var APP: Object;


const logger = getLogger(__filename);

/**
 * Mutes the local participant.
 *
 * @param {boolean} enable - Whether to mute or unmute.
 * @param {MEDIA_TYPE} mediaType - The type of the media channel to mute.
 * @param {boolean} bool - The type of the media channel to mute.
 * @returns {Function}
 */
export function muteLocal(enable: boolean, mediaType: MEDIA_TYPE) {

    return (dispatch: Dispatch<any>, getState: Function) => {
        const isAudio = mediaType === MEDIA_TYPE.AUDIO;

        if (!isAudio && mediaType !== MEDIA_TYPE.VIDEO) {
            logger.error(`Unsupported media type: ${mediaType}`);

            return;
        }

        const state = getState();

        if (!enable) {
            if (isAudio && isLocalParticipantForceMutedAudio(state)) {
                logger.warn('Unmuting audio is blocked: local participant is force muted');

                return;
            }
            if (!isAudio && isLocalParticipantForceMutedVideo(state)) {
                logger.warn('Unmuting video is blocked: local participant is force muted');

                return;
            }
        }

        sendAnalytics(createToolbarEvent(isAudio ? AUDIO_MUTE : VIDEO_MUTE, { enable }));
        dispatch(isAudio ? setAudioMuted(enable, /* ensureTrack */ true)
            : setVideoMuted(enable, mediaType, VIDEO_MUTISM_AUTHORITY.USER, /* ensureTrack */ true));

        // FIXME: The old conference logic as well as the shared video feature
        // still rely on this event being emitted.
        typeof APP === 'undefined'
            || APP.UI.emitEvent(isAudio ? UIEvents.AUDIO_MUTED : UIEvents.VIDEO_MUTED, enable, true);
    };
}

/**
 * Mutes the remote participant with the given ID.
 *
 * @param {string} participantId - ID of the participant to mute.
 * @param {MEDIA_TYPE} mediaType - The type of the media channel to mute.
 * @param {?boolean} bool - The type of the media channel to mute.
 * @returns {Function}
 */
export function muteRemote(participantId: string, mediaType: MEDIA_TYPE, bool: ?boolean) {


    return (dispatch: Dispatch<any>) => {
        if (mediaType !== MEDIA_TYPE.AUDIO && mediaType !== MEDIA_TYPE.VIDEO) {
            logger.error(`Unsupported media type: ${mediaType}`);

            return;
        }
        sendAnalytics(createRemoteMuteConfirmedEvent(participantId, mediaType));
        dispatch(muteRemoteParticipant(participantId, mediaType, bool));
    };
}

/**
 * Mutes all participants.
 *
 * @param {Array<string>} exclude - Array of participant IDs to not mute.
 * @param {MEDIA_TYPE} mediaType - The media type to mute.
 * @returns {Function}
 */
export function muteAllParticipants(exclude: Array<string>, mediaType: MEDIA_TYPE) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const localId = getLocalParticipant(state)?.id;
        const participants = getParticipants(state);
        const participantIds = participants.map(p => p.id);

        participantIds
            .filter(id => !exclude.includes(id))
            .map(id => {
                if (id === localId) {
                    return muteLocal(true, mediaType);
                }

                return muteRemote(id, mediaType, true);
            })
            .map(dispatch);
    };
}

/**
 * Mutes all participants.
 *
 * @param {Array<string>} exclude - Array of participant IDs to not mute.
 * @param {MEDIA_TYPE} mediaType - The media type to mute.
 * @param {boolean} bool - The type of the media channel to mute.
 * @returns {Function}
 */
export function forceMuteAllParticipants(exclude: Array<string>, mediaType: MEDIA_TYPE) {

    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const confSetting = state['features/base/jwt']?.confSettings;
        const conference = state['features/base/conference'].conference;


        const localId = getLocalParticipant(state).id;
        const { lockAudio, lockVideo } = state['features/base/conference'];
        const participants = state['features/base/participants'];

        const remoteLockAudios = Object.values(lockAudio).filter(
            participantId => participants.find(p => p.local) && participants.find(p => p.id === participantId)?.role !== 'moderator'
        );

        const remoteLockVideos = Object.values(lockVideo).filter(
            participantId => participants.find(p => p.local) && participants.find(p => p.id === participantId)?.role !== 'moderator'
        );

        const isAudioMuteAll = Object.values(remoteLockAudios).some(value => Boolean(value));
        const isVideoMuteAll = Object.values(remoteLockVideos).some(value => Boolean(value));

        const moderatorPart = state['features/base/participants']
        .map(p => {
            return {
                role: p.role,
                id: p.id };
        });


        if (mediaType === MEDIA_TYPE.AUDIO) {
            const targetMuteState = !isAudioMuteAll;

            moderatorPart
                .filter(p => !exclude.includes(p.id))
                .filter(p => p.role !== 'moderator')
                .map(({ id }) => {
                    if (id === localId) {
                        return muteLocal(true, mediaType);
                    }

                    return forcemuteRemote(id, mediaType, targetMuteState, true);
                })
                .map(dispatch);
            dispatch(moderatorForceMutedUsAll(mediaType, targetMuteState));
            if (confSetting.FORCE_MUTE_PARTICIPANTS === 1) {
                dispatch(moderatorunclockAudio());
                conference.sendMessage({
                    type: MODERATOR_UNLOCK_AUDIO,
                    senderId: localId
                });
            }

        } else {
            const targetMuteState = !isVideoMuteAll;

            moderatorPart
                .filter(p => !exclude.includes(p.id))
                .filter(p => p.role !== 'moderator')
                .map(({ id }) => {
                    if (id === localId) {
                        return muteLocal(true, mediaType);
                    }

                    return forcemuteRemote(id, mediaType, targetMuteState, true);
                })
                .map(dispatch);

            dispatch(moderatorForceMutedUsAll(mediaType, targetMuteState));
            if (confSetting.FORCE_VIDEO_MUTE_PARTICIPANTS === 1) {
                dispatch(moderatorunclockVideo());
                conference.sendMessage({
                    type: MODERATOR_UNLOCK_VIDEO,
                    senderId: localId
                });
            }
        }

    };
}

/**
 * Mutes the remote participant with the given ID.
 *
 * @param {string} participantId - ID of the participant to mute.
 * @param {MEDIA_TYPE} mediaType - The type of the media channel to mute.
 * @param {?boolean} bool - The type of the media channel to mute.
 * @returns {Function}
 */
export function forcemuteRemote(participantId: string, mediaType: MEDIA_TYPE, bool: ?boolean, isMuteAll?: boolean) {


    return (dispatch: Dispatch<any>, getState: Function) => {
        if (mediaType !== MEDIA_TYPE.AUDIO && mediaType !== MEDIA_TYPE.VIDEO) {
            logger.error(`Unsupported media type: ${mediaType}`);

            return;
        }
        const state = getState();
        const { lockAudio, lockVideo } = state['features/base/conference'];


        if (mediaType === MEDIA_TYPE.AUDIO) {

            if (bool) {
                sendAnalytics(createRemoteMuteConfirmedEvent(participantId, mediaType));
                dispatch(forcemuteRemoteParticipant(participantId, mediaType, bool, isMuteAll));
            } else if (bool === undefined && !lockAudio[`${participantId}`]) {

                sendAnalytics(createRemoteMuteConfirmedEvent(participantId, mediaType));
                dispatch(forcemuteRemoteParticipant(participantId, mediaType, true, isMuteAll));
            } else {

                sendAnalytics(createRemoteMuteConfirmedEvent(participantId, mediaType));
                dispatch(forcemuteRemoteParticipant(participantId, mediaType, !lockAudio[`${participantId}`], isMuteAll));
            }
        } else if (bool) {
            sendAnalytics(createRemoteMuteConfirmedEvent(participantId, mediaType));
            dispatch(forcemuteRemoteParticipant(participantId, mediaType, bool, isMuteAll));
        } else if (bool === undefined && !lockVideo[`${participantId}`]) {
            sendAnalytics(createRemoteMuteConfirmedEvent(participantId, mediaType));
            dispatch(forcemuteRemoteParticipant(participantId, mediaType, true, isMuteAll));
        } else {

            sendAnalytics(createRemoteMuteConfirmedEvent(participantId, mediaType));
            dispatch(forcemuteRemoteParticipant(participantId, mediaType, !lockVideo[`${participantId}`], isMuteAll));
        }
    };
}
